import { TAB_BOTTOM_SPACING } from '@/components/layout/tab-screen-wrapper';
import { ActivityCard } from '@/features/activity/components/activity-card';
import { useAppTheme } from '@/providers/theme-provider';
import type { FeedItem } from '@/types/feed';
import React, { useCallback } from 'react';
import { ActivityIndicator, FlatList, RefreshControl, View } from 'react-native';

interface FeedListProps {
  items: FeedItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  onLike?: (checkInId: string, currentlyLiked: boolean) => void;
  ListEmptyComponent?: React.ReactElement;
}

export function FeedList({
  items,
  hasNextPage,
  isFetchingNextPage,
  isRefetching,
  onLoadMore,
  onRefresh,
  onLike,
  ListEmptyComponent,
}: FeedListProps) {
  const theme = useAppTheme();

  const renderItem = useCallback(
    ({ item }: { item: FeedItem }) => (
      <ActivityCard item={item} onLike={onLike} />
    ),
    [onLike],
  );

  const keyExtractor = useCallback(
    (item: FeedItem) => item.checkIn.id,
    [],
  );

  const ListFooterComponent = isFetchingNextPage ? (
    <View className="py-4 items-center">
      <ActivityIndicator color={theme.colors.primary} />
    </View>
  ) : null;

  return (
    <FlatList
      testID="feed-list"
      data={items}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.5}
      ListFooterComponent={ListFooterComponent}
      ListEmptyComponent={ListEmptyComponent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isRefetching && !isFetchingNextPage}
          onRefresh={onRefresh}
          tintColor={theme.colors.primary}
        />
      }
      contentContainerStyle={{
        paddingBottom: TAB_BOTTOM_SPACING,
        paddingHorizontal: 16,
        paddingTop: 16,
      }}
    />
  );
}
